import type { QuestionLearner } from '~/gql/questions/questions'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'
import TablePagination from '~/pages/backoffice/TablePagination'
import { SearchInput } from '~/pages/backoffice/TableToolbar'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'

type Props = {
  learners: QuestionLearner[]
  /** Résumé des filtres de l'écran : quand il change, retour en page 1 (#198). */
  filtersKey: string
  onOpen: (learner: QuestionLearner) => void
}

/**
 * Les apprenants qui ont répondu ; chacun ouvre ses réponses, tentative par
 * tentative (#159). Une recherche resserre la liste quand elle s'allonge, et
 * elle se pagine, 10 par défaut (#198).
 */
function QuestionLearners({ learners, filtersKey, onOpen }: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const needle = search.trim().toLocaleLowerCase()
  const shown = learners
    .filter(learner => !needle || `${userDisplayName(learner)} ${learner.email}`.toLocaleLowerCase().includes(needle))
    .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)))
  const { pageRows, pagination } = useClientPagination(shown, `${filtersKey}|${needle}`)

  return (
    <section className="flex flex-col gap-2" aria-labelledby="question-learners" {...tourAnchor('questions.learners')}>
      <h2 id="question-learners" className="text-sm font-semibold">{t('questions.learners', { count: learners.length })}</h2>
      {learners.length > 0 && <SearchInput value={search} onChange={e => setSearch(e.target.value)} />}
      <ul className="flex flex-wrap gap-2">
        {pageRows.map(learner => (
          <li key={learner.id}>
            <Button variant="outline" size="sm" onClick={() => onOpen(learner)}>
              {userDisplayName(learner)}
            </Button>
          </li>
        ))}
      </ul>
      {shown.length > 0 && <TablePagination {...pagination} />}
    </section>
  )
}

export default QuestionLearners
